import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

// Solana Brand Colors
const SOL_PURPLE = '#9945FF';
const SOL_GREEN = '#14F195';
const SOL_DARK = '#000000';
const SOL_LIGHT = '#FFFFFF';
const SOL_BLACK = '#000000';

const NavigationBar = styled.div`
  width: 100%;
  background: ${SOL_BLACK};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
`;

const NavigationContainer = styled.div`
  width: 100%;
  padding: 16px 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  
  @media (max-width: 768px) {
    padding: 12px 15px;
    gap: 12px;
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  
  @media (max-width: 700px) {
    flex: 1;
  }
`;

const SolanaLogo = styled.img`
  height: 40px;
  width: auto;
  object-fit: contain;
  
  @media (max-width: 700px) {
    height: 32px;
  }
`;

const LogoText = styled.span`
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 20px;
  color: ${SOL_LIGHT};
  letter-spacing: -0.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 700px) {
    font-size: 14px;
    white-space: normal;
    line-height: 1.2;
  }
`;

const BetaBadge = styled.sup`
  font-size: 10px;
  font-weight: 600;
  color: ${SOL_GREEN};
  margin-left: 4px;
  vertical-align: super;
  letter-spacing: 0.5px;
  
  @media (max-width: 700px) {
    font-size: 8px;
    margin-left: 2px;
  }
`;

const WalletSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const WalletBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(153, 69, 255, 0.1);
  border: 1px solid rgba(153, 69, 255, 0.3);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(153, 69, 255, 0.2);
    border-color: rgba(153, 69, 255, 0.5);
  }
`;

const AddressText = styled.span`
  color: ${SOL_LIGHT};
  font-size: 14px;
  font-weight: 500;
  font-family: 'Inter', monospace;
  
  @media (max-width: 700px) {
    font-size: 12px;
  }
`;

const ConnectButton = styled.button`
  background: ${SOL_PURPLE};
  color: ${SOL_LIGHT};
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #7a35cc;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(153, 69, 255, 0.3);
  }
  
  @media (max-width: 700px) {
    padding: 6px 12px;
    font-size: 12px;
  }
`;

const SolanaNavBar: React.FC = () => {
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);

  // Check for existing Phantom connection
  useEffect(() => {
    const checkPhantomConnection = async () => {
      const phantom = (window as any).phantom?.solana || (window as any).solana;
      
      if (phantom?.isPhantom) {
        try {
          // Try to get already connected wallet
          if (phantom.isConnected && phantom.publicKey) {
            setWalletAddress(phantom.publicKey.toString());
          } else {
            // Try auto-connect if trusted
            const response = await phantom.connect({ onlyIfTrusted: true });
            if (response?.publicKey) {
              setWalletAddress(response.publicKey.toString());
            }
          }
        } catch (e) {
          // Not connected or not trusted
          console.log('Phantom not connected');
        }
      }
      
      // Also check localStorage for stored wallet
      const storedWallet = localStorage.getItem('connectedSolanaWallet') || 
                          localStorage.getItem('mavrk_local_solana_wallet');
      if (storedWallet) {
        setWalletAddress(storedWallet);
      }
    };

    checkPhantomConnection();

    // Listen for Phantom account changes
    const handleAccountChange = (publicKey: any) => {
      if (publicKey) {
        setWalletAddress(publicKey.toString());
        localStorage.setItem('connectedSolanaWallet', publicKey.toString());
      } else {
        setWalletAddress(null);
        localStorage.removeItem('connectedSolanaWallet');
        localStorage.removeItem('mavrk_local_solana_wallet');
      }
    };

    const phantom = (window as any).phantom?.solana || (window as any).solana;
    if (phantom?.isPhantom) {
      phantom.on('accountChanged', handleAccountChange);
      phantom.on('disconnect', () => {
        setWalletAddress(null);
        localStorage.removeItem('connectedSolanaWallet');
        localStorage.removeItem('mavrk_local_solana_wallet');
      });
    }

    return () => {
      if (phantom?.isPhantom) {
        phantom.off('accountChanged', handleAccountChange);
        phantom.off('disconnect');
      }
    };
  }, []);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      // Check for Phantom
      const phantom = (window as any).phantom?.solana || (window as any).solana;
      
      if (!phantom?.isPhantom) {
        // Check for Solflare
        const solflare = (window as any).solflare;
        if (solflare?.isSolflare) {
          const response = await solflare.connect();
          if (response?.publicKey) {
            const address = response.publicKey.toString();
            setWalletAddress(address);
            localStorage.setItem('connectedSolanaWallet', address);
            return;
          }
        }
        
        // No wallet found - open Phantom install page
        alert('No Solana wallet detected.\n\nPlease install Phantom or Solflare browser extension and refresh the page.');
        window.open('https://phantom.app/', '_blank');
        return;
      }

      // Connect to Phantom
      const response = await phantom.connect();
      if (response?.publicKey) {
        const address = response.publicKey.toString();
        setWalletAddress(address);
        localStorage.setItem('connectedSolanaWallet', address);
      }
    } catch (err: any) {
      if (err.code === 4001) {
        console.log('User rejected wallet connection');
      } else {
        console.error('Failed to connect wallet:', err);
        alert('Failed to connect. Make sure your Solana wallet is unlocked.');
      }
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = () => {
    setWalletAddress(null);
    localStorage.removeItem('connectedSolanaWallet');
    localStorage.removeItem('mavrk_local_solana_wallet');
    
    // Also disconnect from Phantom if connected
    const phantom = (window as any).phantom?.solana || (window as any).solana;
    if (phantom?.isPhantom && phantom.isConnected) {
      phantom.disconnect();
    }
  };

  return (
    <NavigationBar>
      <NavigationContainer>
        <LogoSection onClick={() => window.location.href = '/solana'}>
          <SolanaLogo src="/landing-assets/Solana.png" alt="Solana" />
          <LogoText>
            Mavrk on Solana<BetaBadge>[BETA]</BetaBadge>
          </LogoText>
        </LogoSection>

        <WalletSection>
          {walletAddress && (
            <WalletBadge onClick={handleDisconnect} title="Click to disconnect wallet">
              <AddressText>
                {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
              </AddressText>
            </WalletBadge>
          )}
          {!walletAddress && (
            <ConnectButton onClick={handleConnect} disabled={isConnecting}>
              {isConnecting ? 'Connecting...' : 'Connect Wallet'}
            </ConnectButton>
          )}
        </WalletSection>
      </NavigationContainer>
    </NavigationBar>
  );
};

export default SolanaNavBar;

